/**
 * GroupFormWizard.jsx
 * Formulario de Grupo em Etapas Premium
 *
 * 2 Passos:
 * 1. Basico - Nome, Grupo Pai (obrigatorio)
 * 2. Atributos - Atributos customizados (opcional)
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box, Grid, Typography } from '@mui/material';
import {
  Folder as FolderIcon,
  Tune as TuneIcon,
  CheckCircle as ReviewIcon,
} from '@mui/icons-material';

import { useTranslation } from '../../common/components/LocalizationProvider';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { groupsActions } from '../../store';
import fetchOrThrow from '../../common/util/fetchOrThrow';

import PremiumWizard from '../../common/components/premium/PremiumWizard';
import PremiumInput from '../../common/components/premium/PremiumInput';
import SelectField from '../../common/components/SelectField';
import EditAttributesAccordion from './EditAttributesAccordion';
import useCommonDeviceAttributes from '../../common/attributes/useCommonDeviceAttributes';
import useGroupAttributes from '../../common/attributes/useGroupAttributes';
import { getWizardColors } from '../../common/components/premium/WizardStepTemplates';

// ============================================================================
// STEP 1: INFORMACOES BASICAS
// ============================================================================

const BasicInfoStep = ({ data, updateData, errors }) => {
  const t = useTranslation();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <PremiumInput
            label={`${t('sharedName')} *`}
            value={data.name || ''}
            onChange={(e) => updateData({ name: e.target.value })}
            error={!!errors?.name}
            helperText={errors?.name}
            autoFocus
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <SelectField
            fullWidth
            value={data.groupId || ''}
            onChange={(e) => updateData({ groupId: e.target.value ? Number(e.target.value) : null })}
            endpoint="/api/groups"
            label={t('groupParent')}
            className="premium-input"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

// ============================================================================
// STEP 2: ATRIBUTOS
// ============================================================================

const AttributesStep = ({ data, updateData }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  const commonDeviceAttributes = useCommonDeviceAttributes(t);
  const groupAttributes = useGroupAttributes(t);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          p: 2,
          borderRadius: '10px',
          backgroundColor: colors.surfaceHover,
          border: `1px solid ${colors.borderSubtle}`,
        }}
      >
        <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: '13px' }}>
          {t('wizardAttributesHelp')}
        </Typography>
      </Box>

      <EditAttributesAccordion
        attributes={data.attributes}
        setAttributes={(attributes) => updateData({ attributes })}
        definitions={{ ...commonDeviceAttributes, ...groupAttributes }}
      />
    </Box>
  );
};

// ============================================================================
// STEP 3: REVISAO
// ============================================================================

const ReviewStep = ({ data }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  const InfoRow = ({ label, value }) => {
    if (!value && value !== 0) return null;
    return (
      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '11px' }}>
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: colors.textPrimary, fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}
        >
          {value}
        </Typography>
      </Box>
    );
  };

  const attributeCount = data.attributes ? Object.keys(data.attributes).length : 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 2,
          backgroundColor: colors.accentSubtle,
          borderRadius: tokens?.radius?.md || '8px',
          border: `1px solid ${colors.accent}30`,
        }}
      >
        <ReviewIcon sx={{ color: colors.accent }} />
        <Typography sx={{ color: colors.textPrimary }}>
          {t('wizardReviewConfirm')}
        </Typography>
      </Box>

      <Box
        sx={{
          backgroundColor: colors.surfaceElevated,
          borderRadius: tokens?.radius?.lg || '12px',
          border: `1px solid ${colors.borderSubtle}`,
          p: 2.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: colors.textPrimary,
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: '11px',
            letterSpacing: '0.05em',
            mb: 2,
          }}
        >
          {t('sharedRequired')}
        </Typography>
        <InfoRow label={t('sharedName')} value={data.name} />
        <InfoRow label={t('groupParent')} value={data.groupId ? `ID: ${data.groupId}` : '-'} />
        <InfoRow label={t('sharedAttributes')} value={attributeCount > 0 ? `${attributeCount} ${t('sharedAttributes').toLowerCase()}` : '-'} />
      </Box>
    </Box>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const GroupFormWizard = ({ group, onSave, onCancel }) => {
  const t = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const steps = [
    {
      label: t('wizardStepBasic'),
      title: t('sharedRequired'),
      description: t('wizardGroupBasicDescription') || 'Dados do grupo',
      icon: <FolderIcon sx={{ fontSize: 18 }} />,
      component: BasicInfoStep,
      validate: (data) => {
        const errors = {};
        if (!data.name?.trim()) errors.name = t('sharedRequired');
        return Object.keys(errors).length ? errors : true;
      },
      getSummary: (data) => [
        { label: t('sharedName'), value: data.name },
      ],
    },
    {
      label: t('sharedAttributes'),
      title: t('sharedAttributes'),
      description: t('wizardAttributesDescription'),
      icon: <TuneIcon sx={{ fontSize: 18 }} />,
      component: AttributesStep,
      optional: true,
    },
    {
      label: t('wizardStepReview'),
      title: t('wizardReviewTitle'),
      description: t('wizardReviewDescription'),
      icon: <ReviewIcon sx={{ fontSize: 18 }} />,
      component: ReviewStep,
    },
  ];

  const handleComplete = async (data) => {
    try {
      const url = group?.id ? `/api/groups/${group.id}` : '/api/groups';
      const method = group?.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const savedGroup = await response.json();
        // Refresh all groups in Redux
        const groupsResponse = await fetchOrThrow('/api/groups');
        dispatch(groupsActions.refresh(await groupsResponse.json()));
        onSave?.(savedGroup);
        navigate('/settings/groups');
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || t('errorGeneric'));
      }
    } catch (error) {
      console.error('Save error:', error);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    navigate('/settings/groups');
  };

  const initialData = group || {
    name: '',
    groupId: null,
    attributes: {},
  };

  return (
    <PremiumWizard
      steps={steps}
      initialData={initialData}
      onComplete={handleComplete}
      onCancel={handleCancel}
      storageKey={group?.id ? null : 'group-wizard-draft'}
      completeButtonText={group?.id ? t('sharedSave') : t('sharedAdd')}
      nextButtonText={t('wizardNext')}
      backButtonText={t('sharedBack')}
      cancelButtonText={t('sharedCancel')}
      skipButtonText={t('wizardSkip')}
      showSummary
    />
  );
};

export default GroupFormWizard;
